/**
 * usePurchaseInvoiceSave Hook
 * Alış faturası kaydetme işlemleri
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ownAuthSession from '../../../../services/auth/ownAuthSession';
import { getCompanyInfo } from '../../../../services/migration/companyDataService';
import { accountingApi } from '../../../../config/apiClient';
import { ACCOUNTING_API } from '../../../../config/api';
import { Account, InvoiceItem } from '../components/types/invoice.types';
import { invoiceLotPayload } from '../utils/invoiceLot';
import { useAuth } from '../../../../contexts/AuthContext';
import { linkImportedIncomingOrderInvoice } from '../../../../services/ecommerceApi';

interface UsePurchaseInvoiceSaveProps {
  companyId: string | null;
  editingInvoiceId: string | null;
  ettnFromUrl: string | null;
  selectedAccount: Account | null;
  selectedItems: InvoiceItem[];
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  notes: string;
  /** PYS: belgenin bağlı olduğu proje (opsiyonel). */
  projectId?: number | null;
  /** Kâr-Zarar gider türü; null → stok/mal alımı (maliyet). */
  expenseCategoryId?: number | null;
  /** Faturanın ait olduğu şube; null → merkez (şubesiz). */
  branchId?: string | null;
  currency: string;
  exchangeRate: string;
  tevkifatRate: string;
  tevkifatType: string;
  stopajRate: number;
  /** İndirim giriş modu (kolon-seviyesi) — fatura başlığında saklanır */
  discountMode?: 'PERCENT' | 'AMOUNT';
  grandTotal: number;
  originalInvoiceTotal: string;
  invoiceDifference: number;
  eInvoiceSupplierInfo: any;

  // ✅ İade (alış-iadesi) ilişkisi — gönderilmiş bir alış faturasından iade oluşturuluyorsa
  eInvoiceTypeCode?: string | null;
  returnedInvoiceId?: string | null;
  returnedInvoiceNumber?: string | null;
  returnedInvoiceDate?: string | null;
  /** İçe aktarılan / iade gönderiminde kullanılan entegratör ("edm" | "finansbank") */
  eInvoiceProvider?: string | null;

  // ✅ Kaynak belge ilişkileri (dönüşüm takibi)
  sourceOrderId?: string | null;
  sourceOrderNumber?: string | null;
  sourceOfferId?: string | null;
  sourceOfferNumber?: string | null;
  /** B2B gelen alış siparişi — kayıt sonrası faturayı siparişe bağlar. */
  incomingPurchaseOrderId?: string | null;

  // Setters
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setLotWarnings?: (warnings: string[]) => void;
  onLotLine?: (lineNumber: number | null) => void;
  setInvoiceNumber: (number: string) => void;
  setIsStockProcessModalOpen: (open: boolean) => void;
  setStockProcessTotalItems: (count: number) => void;
  setStockProcessCurrentItem: (count: number) => void;
  setStockProcessSuccess: (success: boolean) => void;
  setIsSuccessModalOpen: (open: boolean) => void;
  clearDraftFromLocalStorage: () => void;
}

export const usePurchaseInvoiceSave = (props: UsePurchaseInvoiceSaveProps) => {
  const { companyName } = useAuth();
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  
  const {
    companyId,
    editingInvoiceId,
    ettnFromUrl,
    selectedAccount,
    selectedItems,
    invoiceNumber,
    invoiceDate,
    dueDate,
    notes,
    projectId,
    expenseCategoryId,
    branchId,
    currency,
    exchangeRate,
    tevkifatRate,
    tevkifatType,
    stopajRate,
    discountMode,
    grandTotal,
    originalInvoiceTotal,
    invoiceDifference,
    eInvoiceSupplierInfo,
    eInvoiceTypeCode,
    returnedInvoiceId,
    returnedInvoiceNumber,
    returnedInvoiceDate,
    eInvoiceProvider,
    sourceOrderId,
    sourceOrderNumber,
    sourceOfferId,
    sourceOfferNumber,
    incomingPurchaseOrderId,
    setLoading,
    setError,
    setLotWarnings,
    onLotLine,
    setInvoiceNumber,
    setIsStockProcessModalOpen,
    setStockProcessTotalItems,
    setStockProcessCurrentItem,
    setStockProcessSuccess,
    setIsSuccessModalOpen,
    clearDraftFromLocalStorage,
  } = props;
  
  /**
   * Fatura kaydetme (ATOMİK)
   * Fatura + Stok + Cari tek transaction'da
   */
  const handleSaveInvoice = useCallback(async () => {
    if (!selectedAccount) {
      setError(tt('Lütfen bir cari seçin.', 'Please select an account.'));
      return;
    }
    
    if (selectedItems.length === 0) {
      setError('Lütfen en az bir ürün ekleyin.');
      return;
    }

    // İade (alış-iadesi) tipi faturalarda orijinal alış faturası referansı zorunludur.
    // (Backend de ValidateReturnInvoiceAsync ile aynı kuralı uygular.)
    const isReturnType = ['IADE', 'TEVKIFATIADE', 'YTBIADE'].includes(String(eInvoiceTypeCode || '').toUpperCase());
    if (isReturnType && !returnedInvoiceId) {
      setError(tt(
        'İade faturası için iade edilen orijinal alış faturası seçilmelidir.',
        'A return invoice requires the original purchase invoice to be referenced.'
      ));
      return;
    }

    // Backend'e gitmeden once satir bazli dogrulama yap. Backend'deki
    // [Range] / [Required] validation hatalarini kullaniciya Turkce ve
    // satir bilgisiyle birlikte anlasilir sekilde gosteriyoruz.
    const clientErrors: string[] = [];
    selectedItems.forEach((item, idx) => {
      const lineNo = idx + 1;
      const itemLabel = item.itemName?.trim() || `Satır ${lineNo}`;

      const qty = Number(item.quantity);
      if (!Number.isFinite(qty) || qty <= 0) {
        clientErrors.push(
          `${lineNo}. satır (${itemLabel}): Miktar 0'dan büyük olmalıdır.`
        );
      }

      const up = Number(item.unitPrice);
      if (!Number.isFinite(up) || up < 0) {
        clientErrors.push(
          `${lineNo}. satır (${itemLabel}): Birim fiyat negatif olamaz.`
        );
      }

      const vr = Number(item.vatRate ?? 0);
      if (!Number.isFinite(vr) || vr < 0 || vr > 100) {
        clientErrors.push(
          `${lineNo}. satır (${itemLabel}): KDV oranı 0 ile 100 arasında olmalıdır (girilen: ${item.vatRate}).`
        );
      }

      const dr = Number(item.discountRate ?? 0);
      if (!Number.isFinite(dr) || dr < 0 || dr > 100) {
        clientErrors.push(
          `${lineNo}. satır (${itemLabel}): İskonto oranı 0 ile 100 arasında olmalıdır (girilen: ${item.discountRate}).`
        );
      }

      const sr = Number(item.sctRate ?? 0);
      if (!Number.isFinite(sr) || sr < 0 || sr > 100) {
        clientErrors.push(
          `${lineNo}. satır (${itemLabel}): ÖTV oranı 0 ile 100 arasında olmalıdır (girilen: ${item.sctRate}).`
        );
      }

      if (!item.itemName || !item.itemName.trim()) {
        clientErrors.push(`${lineNo}. satır: Ürün adı zorunludur.`);
      }

      if (!item.unit || !item.unit.toString().trim()) {
        clientErrors.push(`${lineNo}. satır (${itemLabel}): Birim zorunludur.`);
      }
    });

    // Exchange rate sanity check - NaN / 0 / negatif ise backend decimal
    // binding / [Range(0.0001, ...)] validation'ina takilir.
    const er = parseFloat(exchangeRate);
    if (!Number.isFinite(er) || er <= 0) {
      clientErrors.push(`Döviz kuru geçersiz (girilen: "${exchangeRate}"). 0'dan büyük bir sayı olmalıdır.`);
    }

    if (clientErrors.length > 0) {
      const msg = `Lütfen aşağıdaki hataları düzeltin:\n\n• ${clientErrors.join('\n• ')}`;
      setError(msg);
      console.warn('⚠️ [CLIENT] Fatura doğrulama hatası:', clientErrors);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      if (!companyId) {
        setError('Şirket bilgisi bulunamadı.');
        return;
      }
      
      const createdBy = ownAuthSession.getCurrentEmail() || 'System';
      
      // Şirket adını Firebase'den al
      let companyNameForInvoice = 'DEFAULT';
      
      try {
        const companyInfo = await getCompanyInfo(companyId);
        if (companyInfo?.companyName) {
          companyNameForInvoice = companyInfo.companyName;
          console.log('🏢 [ŞİRKET] companyDataService\'den alındı:', companyNameForInvoice);
        }
      } catch (err) {
        console.error('❌ [ŞİRKET] Şirket adı alınırken hata:', err);
      }
      
      console.log('🔄 [ATOMİK] Alış faturası kaydediliyor...');
      
      // Modal'ı aç
      setStockProcessTotalItems(selectedItems.length);
      setStockProcessCurrentItem(0);
      setStockProcessSuccess(false);
      setIsStockProcessModalOpen(true);
      
      // API DTO hazırla
      const createInvoiceCompleteDto = {
        companyId: companyId,
        projectId: projectId ?? null,
        expenseCategoryId: expenseCategoryId ?? null,
        companyName: companyNameForInvoice,
        manualInvoiceNumber: invoiceNumber.trim() || null,
        invoiceType: 'PURCHASE',
        accountId: String(selectedAccount.id),
        invoiceDate: new Date(invoiceDate).toISOString(),
        dueDate: dueDate ? new Date(dueDate).toISOString() : null,
        currency: currency,
        exchangeRate: parseFloat(exchangeRate),
        discountType: discountMode || 'PERCENT',
        tevkifatRate: tevkifatRate && tevkifatRate !== "0" ? tevkifatRate : null,
        tevkifatType: tevkifatType || null,
        stopajRate: stopajRate || null,
        originalInvoiceTotal: originalInvoiceTotal && originalInvoiceTotal.trim() !== '' 
          ? parseFloat(originalInvoiceTotal.replace(/\./g, '').replace(',', '.')) 
          : null,
        notes: notes || null,
        description: `Alış Faturası - ${selectedAccount.companyName}`,
        branchId: branchId || null,
        // ✅ PR3 (Invoice Seller Snapshot Refactor): Manuel akış → backend Companies fallback'e düşer
        screenType: 'Manual',
        createdBy: createdBy,
        eInvoiceETTN: ettnFromUrl || null,
        eInvoiceProfileType: eInvoiceSupplierInfo?.profileType || null,
        // ✅ İçe aktarılan / iade gönderiminde kullanılan entegratör
        eInvoiceProvider: eInvoiceProvider || null,
        // ✅ İade (alış-iadesi) bilgileri — tedarikçiye giden iade e-faturası için
        eInvoiceTypeCode: eInvoiceTypeCode || null,
        returnedInvoiceId: returnedInvoiceId || null,
        returnedInvoiceNumber: returnedInvoiceNumber || null,
        returnedInvoiceDate: returnedInvoiceDate || null,
        // ✅ Kaynak belge ilişkileri (dönüşüm takibi)
        sourceOfferId: sourceOfferId || null,
        sourceOfferNumber: sourceOfferNumber || null,
        sourceOrderId: sourceOrderId || null,
        sourceOrderNumber: sourceOrderNumber || null,
        sourceWaybillId: null,
        sourceWaybillNumber: null,
        items: selectedItems.map((item, index) => ({
          itemId: item.itemId.startsWith('manual_') ? null : item.itemId,
          itemName: item.itemName,
          isManualItem: item.itemId.startsWith('manual_'),
          quantity: item.quantity,
          unit: item.unit,
          unitPrice: item.unitPrice,
          enteredPrice: item.enteredPrice,
          priceType: item.priceType || 'VAT_EXCLUDED',
          vatRate: item.vatRate,
          sctRate: item.sctRate || 0,
          discountRate: item.discountRate || 0,
          discountAmount: item.discountAmount || 0,
          warehouseId: item.warehouseId || null,
          lineNumber: index + 1,
          ...invoiceLotPayload(item),
        }))
      };
      
      console.log('📤 [ATOMİK] Backend\'e gönderiliyor:', createInvoiceCompleteDto);
      
      // Düzenleme modunda UPDATE, yeni faturada CREATE
      const url = editingInvoiceId
        ? ACCOUNTING_API.INVOICE_UPDATE(editingInvoiceId)
        : ACCOUNTING_API.INVOICE_CREATE_COMPLETE;
      const { data: result } = await accountingApi.post(url, createInvoiceCompleteDto);
      
      if (!result.success) {
        console.error('❌ [BACKEND HATA] Response:', result);
        
        let errorMsg = result.message || 'Fatura kaydedilemedi';
        
        if (result.errors) {
          const errorDetails = Object.entries(result.errors)
            .map(([field, messages]: [string, any]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
            .join('\n');
          errorMsg = `Validation Hatası:\n${errorDetails}`;
        }
        
        if (result.rollback) {
          errorMsg = `İşlem başarısız ve geri alındı: ${errorMsg}`;
        }
        
        throw new Error(errorMsg);
      }
      
      console.log('✅✅✅ [ATOMİK] TÜM İŞLEMLER BAŞARILI:', result.data);
      setLotWarnings?.(result.lotWarnings ?? result.LotWarnings ?? []);
      onLotLine?.(null);

      if (!editingInvoiceId && incomingPurchaseOrderId) {
        const createdInvoiceId = result.data?.id || result.data?.invoiceId;
        if (createdInvoiceId) {
          try {
            await linkImportedIncomingOrderInvoice(incomingPurchaseOrderId, createdInvoiceId);
          } catch (linkErr: any) {
            console.error('❌ [B2B] İçe aktarılan fatura siparişe bağlanamadı:', linkErr);
            setError(
              linkErr?.response?.data?.message
              || 'Fatura kaydedildi ama B2B siparişe bağlanamadı. Gelen siparişten tekrar deneyin.'
            );
          }
        }
      }
      
      // Backend'den gelen fatura numarasını güncelle
      if (result.data?.invoiceNumber) {
        setInvoiceNumber(result.data.invoiceNumber);
      }
      
      // Taslağı sil
      clearDraftFromLocalStorage();
      
      // Modal'ı güncelle - başarılı
      setStockProcessCurrentItem(selectedItems.length);
      setStockProcessSuccess(true);
      
      // 2 saniye sonra başarı modalını göster
      setTimeout(() => {
        setIsStockProcessModalOpen(false);
        setIsSuccessModalOpen(true);
      }, 2000);
      
    } catch (err: any) {
      console.error('❌ [ATOMİK] Fatura kaydetme hatası:', err);

      // Backend response body'sini (ProblemDetails / errors) ayrıntılı göster.
      // [ApiController] model binding fail'inde ValidationProblemDetails döner:
      // { type, title, status: 400, errors: { FieldName: ["..."] } }
      let errorMessage = err.message || 'Fatura kaydedilirken bir hata oluştu.';
      const responseData = err?.response?.data;
      if (responseData) {
        console.error('🔴 [ATOMİK] Backend response body:', responseData);
        if (responseData.errors && typeof responseData.errors === 'object') {
          console.error('🔴 [ATOMİK] Validation errors:', responseData.errors);

          // .NET'ten gelen teknik alan isimlerini (ör. "Items[3].VatRate")
          // Turkce ve satir bilgisiyle kullaniciya sun.
          const fieldLabels: Record<string, string> = {
            VatRate: 'KDV oranı',
            SctRate: 'ÖTV oranı',
            DiscountRate: 'İskonto oranı',
            DiscountAmount: 'İskonto tutarı',
            Quantity: 'Miktar',
            UnitPrice: 'Birim fiyat',
            EnteredPrice: 'Girilen fiyat',
            ItemName: 'Ürün adı',
            Unit: 'Birim',
            WarehouseId: 'Depo',
            AccountId: 'Cari hesap',
            InvoiceType: 'Fatura tipi',
            InvoiceDate: 'Fatura tarihi',
            DueDate: 'Vade tarihi',
            Currency: 'Para birimi',
            ExchangeRate: 'Döviz kuru',
            CompanyId: 'Şirket',
            CreatedBy: 'Kullanıcı',
            Items: 'Ürünler',
          };

          const humanizeField = (rawField: string): string => {
            // Ornek: "Items[3].VatRate" -> "4. satırdaki KDV oranı"
            // Ornek: "$.items[0].quantity" -> "1. satırdaki Miktar"
            const itemsMatch = rawField.match(/Items?\[(\d+)\]\.(\w+)/i);
            if (itemsMatch) {
              const lineNo = parseInt(itemsMatch[1], 10) + 1;
              const fieldRaw = itemsMatch[2];
              const fieldTr = fieldLabels[fieldRaw] || fieldRaw;
              const lineItem = selectedItems[lineNo - 1];
              const itemLabel = lineItem?.itemName?.trim();
              return itemLabel
                ? `${lineNo}. satır (${itemLabel}) - ${fieldTr}`
                : `${lineNo}. satırdaki ${fieldTr}`;
            }
            // Ornek: "$.ExchangeRate" -> "Döviz kuru"
            const cleaned = rawField.replace(/^\$\./, '').replace(/^\./, '');
            return fieldLabels[cleaned] || cleaned;
          };

          const humanizeMessage = (rawField: string, msg: string): string => {
            const lower = msg.toLowerCase();
            const fieldRaw = rawField.match(/\.(\w+)$/)?.[1] || rawField;

            // .NET default "The field X must be between A and B" mesajlarini
            // Turkce ve baglam ile yeniden yaz.
            if (lower.includes('must be between')) {
              const rangeMatch = msg.match(/between\s+([\d.]+)\s+and\s+([\d.]+)/i);
              if (rangeMatch) {
                return `${rangeMatch[1]} ile ${rangeMatch[2]} arasında olmalıdır`;
              }
            }
            if (lower.includes('could not be converted') || lower.includes('json value')) {
              return 'geçerli bir sayısal değer değil';
            }
            if (lower.includes('required')) {
              return 'zorunludur';
            }
            // .NET vat/sct icin ozel kural
            if (/vat|sct|discount/i.test(fieldRaw) && lower.includes('100')) {
              return '0 ile 100 arasında olmalıdır';
            }
            return msg;
          };

          const parts = Object.entries(responseData.errors as Record<string, any>)
            .map(([field, messages]) => {
              const msgs = Array.isArray(messages) ? messages : [messages];
              const human = humanizeField(field);
              const text = msgs.map((m) => humanizeMessage(field, String(m))).join(', ');
              return `${human}: ${text}`;
            });

          if (parts.length > 0) {
            errorMessage = `Lütfen aşağıdaki hataları düzeltin:\n\n• ${parts.join('\n• ')}`;
          }
        } else if (responseData.message) {
          errorMessage = responseData.message;
        } else if (responseData.title) {
          errorMessage = responseData.title;
        }
      }

      setError(errorMessage);
      onLotLine?.(typeof (responseData?.lineNumber ?? responseData?.LineNumber) === 'number'
        ? (responseData.lineNumber ?? responseData.LineNumber)
        : null);
      setIsStockProcessModalOpen(false);
    } finally {
      setLoading(false);
    }
  }, [
    companyId,
    editingInvoiceId,
    ettnFromUrl,
    selectedAccount,
    selectedItems,
    invoiceNumber,
    invoiceDate,
    dueDate,
    notes,
    projectId,
    expenseCategoryId,
    branchId,
    currency,
    exchangeRate,
    tevkifatRate,
    tevkifatType,
    stopajRate,
    discountMode,
    grandTotal,
    originalInvoiceTotal,
    invoiceDifference,
    eInvoiceSupplierInfo,
    eInvoiceTypeCode,
    returnedInvoiceId,
    returnedInvoiceNumber,
    returnedInvoiceDate,
    eInvoiceProvider,
    sourceOrderId,
    sourceOrderNumber,
    sourceOfferId,
    sourceOfferNumber,
    incomingPurchaseOrderId,
    setLoading,
    setError,
    setLotWarnings,
    onLotLine,
    setInvoiceNumber,
    setIsStockProcessModalOpen,
    setStockProcessTotalItems,
    setStockProcessCurrentItem,
    setStockProcessSuccess,
    setIsSuccessModalOpen,
    clearDraftFromLocalStorage,
    tt,
  ]);
  
  return {
    handleSaveInvoice,
  };
};
